import React, { useState, useEffect, useRef } from 'react';
import { PostRequest } from 'components/common/index.js';
import fileDownload from 'js-file-download'
import Constants from 'components/show_drawing/Constants.jsx';

const TABLE_OUTER_STYLE = { border: '1px solid #ddd' };
const TABLE_CELL_STYLE  = { border: '1px solid #ddd', padding: '8px' };
const COMBO_SECTION_STYLE = { marginTop: '10px' };
const COMBO_TITLE_STYLE = { marginBottom: '4px' };
const COMBO_SUBTITLE_STYLE = { color: '#888', marginLeft: '8px' };

// Reads the current values of any `ask_values[<relation>][<name>]` and
// `combo_materials[<relation>][<field>]` inputs (rendered by
// ComboButtonsSection / ComboGuestMaterialChooser) plus the top-level
// hidden `combo_ids` field, and returns them nested the way Rails would
// parse a form-encoded request. Callers merge the result into the JSON POST
// body so params[:ask_values] / params[:combo_materials] land in the shape
// BuildComboGuestInputsService expects.
const readComboStateFromForm = (formRef) => {
  const result = {};
  if (!formRef || !formRef.current) { return result; }

  const formData = new FormData(formRef.current);
  const askValues = {};
  const comboMaterials = {};

  for (const [key, value] of formData.entries()) {
    if (key === 'combo_ids') {
      // RedrawForm.jsx renders an empty combo_ids input when no combo is
      // active; skip that so the download body stays clean.
      if (value !== '') { result.combo_ids = value; }
      continue;
    }
    const askMatch = key.match(/^ask_values\[([^\]]+)\]\[([^\]]+)\]$/);
    if (askMatch) {
      const [, relationId, optionName] = askMatch;
      askValues[relationId] = askValues[relationId] || {};
      askValues[relationId][optionName] = value;
      continue;
    }
    const matMatch = key.match(/^combo_materials\[([^\]]+)\]\[([^\]]+)\]$/);
    if (matMatch) {
      const [, relationId, fieldName] = matMatch;
      comboMaterials[relationId] = comboMaterials[relationId] || {};
      comboMaterials[relationId][fieldName] = value;
    }
  }

  if (Object.keys(askValues).length > 0)      { result.ask_values = askValues; }
  if (Object.keys(comboMaterials).length > 0) { result.combo_materials = comboMaterials; }
  return result;
};

const DownloadConfirmation = ( { id, drawing, currentDrawingOptions, currentUserDrawingOptions, advancedOptionsVisible, fileFormat, dimensionTextschecked, comboEnabled, formRef } ) => {
  const [canDownload, setCanDownload] = useState(true)
  const [presentedOptions, setPresentedOptions] = useState( {} )
  const notesTextRef = useRef();
  const [fileName, setFileName] = useState('')
  const [defaultFileName, setDefaultFileName] = useState('')
  const [fileNameWithDimensions, setFileNameWithDimensions] = useState('')
  const [fileNameWithDimensionsChecked, setFileNameWithDimensionsChecked] = useState(false)
  const [notesVisible, setNotesVisible] = useState(false)
  const [charRemaining, setCharRemaining] = useState(255)
  const [cadetClassName, setCadetClassName] = useState("fa fa-caret-down")
  const [downloadWillCostCredit, setDownloadWillCostCredit] = useState(false)
  const [creditCount, setCreditCount] = useState(0)
  const [couponCode, setCouponCode] = useState("")
  const [downloadWillCostCoupon, setDownloadwillCostCouponCode] = useState(false)
  const [alreadyDownloaded, setAlreadyDownloaded] = useState(false)
  const [activeSubscription, setActiveSubscription] = useState(false)
  const [downloadNotes, setDownloadNotes] = useState("")
  const [loading, setLoading] = useState(false)
  const [comboSections, setComboSections] = useState([])

  const getPresentedOptions = () => {
    setLoading( () => true )
    // /Users/emre/code/boxes/app/controllers/concerns/show_drawing_react_actions.rb
    const data = { ...currentUserDrawingOptions };
    if (comboEnabled) {
      Object.assign(data, readComboStateFromForm(formRef));
    }
    PostRequest({
      url: "/" + id  +  `/params_to_confirm?advanced_options_visible=${advancedOptionsVisible}`,
      data: data,
      callback: (data) => {
        setPresentedOptions( () => data.options_to_confirm )
        setComboSections( () => data.combo_sections || [] )
        setCanDownload( () => data.can_download )
        setFileName( () => data.default_file_name )
        setDefaultFileName( () => data.default_file_name )
        setFileNameWithDimensions( () => data.file_name_with_dimensions )
        setDownloadWillCostCredit( () => data.download_will_cost_credit )
        setCreditCount( () => data.available_credits )
        setDownloadwillCostCouponCode( () => data.download_will_cost_coupon_code )
        setCouponCode( () => data.coupon_code_value )
        setAlreadyDownloaded( () => data.already_downloaded )
        setActiveSubscription( () => data.active_subscription )
        setLoading( () => false )
      }
    })
  }

  const handleFileDimensionsChecked = (e) => {
    setFileNameWithDimensionsChecked( (prev) => !prev )
  }

  const handleAddNotesClick = (e) => {
    e.preventDefault()
    setNotesVisible( (prev) => !prev )
  }

  const handleNotesChange = (e) => {
    var tval = e.target.value,
    tlength = tval.length,
    set = 254,
    remain = parseInt(set - tlength);
    setCharRemaining( () => remain )

    if (remain <= 0 && e.which !== 0 && e.charCode !== 0) {
      e.target.value = (tval).substring(0, tlength - 1);
      return false;
    }

    setDownloadNotes( () => e.target.value )
  }

  const fileNameOnChange = (e) => {
    setFileName( () => e.target.value )
  }
  const fileNameWithExtension = () => {
    return `${fileName}.${fileFormat}`
  }

  const performDownload = (e) => {
    e.preventDefault();

    emitEvent(Constants.drawing_download_started, {id: id})

    const url = `/download/${id}/${fileFormat}`
    const dataToPost = { ...currentUserDrawingOptions };
    dataToPost.authenticity_token = $('meta[name=csrf-token]').attr('content');
    dataToPost.dimension_texts_included = dimensionTextschecked
    dataToPost.download_notes = downloadNotes

    // Work 08: carry active combo state in the download body so the server can
    // produce a combined file. These keys aren't part of currentUserDrawingOptions
    // because they bypass formula_parameters in formula_option_actions.rb.
    if (comboEnabled) {
      Object.assign(dataToPost, readComboStateFromForm(formRef));
    }

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dataToPost)
    };

    fetch(url, options)
      .then( res => res.blob() )
      .then( blob => {
        emitEvent(Constants.drawing_download_finished, {id: id})
        fileDownload(blob, fileNameWithExtension())
        refreshDownloadCounts();
      });
  }

  const downloadForm = () => {
    return <div>
      <div className="well" style={{padding: '10px'}}>
        <input type="checkbox" checked={fileNameWithDimensionsChecked} onChange={handleFileDimensionsChecked} />
        <small> { t('download_confirmation.file_name_include_dimensions') } </small>

        <br />

        <div className="input-group">
          <span className="input-group-addon"> { t('download_confirmation.file_name') } </span>
          <input type="text" size="100" className="form-control" name="file_name" id="file_name" value={fileName} onChange={fileNameOnChange}/>
          <span className="input-group-addon"><span id="file_extension"> .{fileFormat} </span> </span>
        </div>

        <a href="#" onClick={handleAddNotesClick}>
          <small>
            { t('download_confirmation.add_notes')} <i className={cadetClassName} aria-hidden="true"></i>
          </small>
        </a>

       {notesVisible && (
        <span>
          <textarea rows="2" className="form-control" onKeyPress={handleNotesChange} ref={notesTextRef} > </textarea>
          <small><p> {charRemaining} </p></small>
        </span> )}

        {downloadWillCostCredit && <p className="well"
          dangerouslySetInnerHTML={{__html: t('download_confirmation.credit_information').replace('%{credit_count}', creditCount) }}></p>
        }

        {downloadWillCostCoupon && <p className="well"
          dangerouslySetInnerHTML={{__html: t('download_confirmation.coupon_information').replace('%{coupon_value}', couponCode) }}></p>
        }

        {alreadyDownloaded && activeSubscription && <p className="well"
          dangerouslySetInnerHTML={{__html: t('download_confirmation.already_downloaded_without_credit') }}></p>
        }

        {alreadyDownloaded && !activeSubscription && <p className="well"
          dangerouslySetInnerHTML={{__html: t('download_confirmation.already_downloaded_with_credit') }}></p>
        }

      </div>

      <div style={{marginTop: '15px'}}>
        <center>
          <button className="simple-btn button btn btn-default" onClick={performDownload}> { t('download_confirmation.confirm') } </button>
        </center>
      </div>

    </div>
  }

  const cannotDownloadError = () => {
    trackEvent('js_error_download_confirm_but_cannot_download', {
      id: id,
      options: currentDrawingOptions,
    })

    return <div>
      <p> { t('drawing.unexpected_error_happened')  } </p>
    </div>
  }

  const renderOptionsTable = () => {
    if (!presentedOptions || Object.keys(presentedOptions).length === 0) {
      return null;
    }

    return (
      <table className="table table-striped" style={TABLE_OUTER_STYLE}>
        <tbody>
          {Object.entries(presentedOptions).map(([key, value]) => (
            <tr key={key}>
              <td style={TABLE_CELL_STYLE}><strong>{key}</strong></td>
              <td style={TABLE_CELL_STYLE}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  const renderComboSections = () => {
    if (!comboSections || comboSections.length === 0) { return null; }

    return comboSections.map((section) => (
      <div key={section.relation_id} style={COMBO_SECTION_STYLE}>
        <div style={COMBO_TITLE_STYLE}>
          <strong>{section.guest_name}</strong>
          {section.guest_drawing_name && (
            <small style={COMBO_SUBTITLE_STYLE}>{section.guest_drawing_name}</small>
          )}
        </div>
        <table className="table table-striped" style={TABLE_OUTER_STYLE}>
          <tbody>
            {Object.entries(section.options_to_confirm).map(([label, value]) => (
              <tr key={label}>
                <td style={TABLE_CELL_STYLE}><strong>{label}</strong></td>
                <td style={TABLE_CELL_STYLE}>{String(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ));
  }

  // useEffect
  useEffect(getPresentedOptions, [ currentDrawingOptions ]);
  useEffect( () => {
    setFileName( () => fileNameWithDimensionsChecked ? fileNameWithDimensions : defaultFileName )
  }, [ fileNameWithDimensionsChecked ]);
  useEffect( () => {
    setCadetClassName( () => notesVisible ?  'fa fa-caret-up' : 'fa fa-caret-down' )
    $(notesTextRef.current).focus();
  }, [ notesVisible ] )
  // useEffect


  return (
    <div>
      <div className="promo-text">
        { t('download_confirmation.title').replace('%{name}', drawing.catalog_name_as_title) }
      </div>
      <hr />

     <div className="">
      <p>
        { t('download_confirmation.information').replace('%{name}', drawing.catalog_name_as_title) }
      </p>
      </div>

    <div className="">
      {loading && <img src="https://d2atdwxjx7uc4i.cloudfront.net/images/bx_loader.gif" width="30px"/> }
      {!loading && renderOptionsTable()}
      {!loading && renderComboSections()}
      <div style={{marginTop: '15px'}}>
      { canDownload && downloadForm() }
      { !canDownload && cannotDownloadError() }
      </div>
    </div>

  </div>
  )
}

export default DownloadConfirmation;

